/**
 * The diet's candidates, in the two forms the exams office actually files
 * things in: a spreadsheet to work from, and a document to sign and put in
 * front of a board.
 *
 * Two listings, never "whichever tab happens to be open":
 *
 *   approved  who is sitting. A numbered listing of candidates is a statement
 *             about that; run over drafts and rejections the same document
 *             looks official and says something untrue.
 *
 *   paid      who has settled the fee — everyone with a payment date behind
 *             them, not only those still parked at the `paid` status. It adds
 *             what a payment list is read for: the reference, the amount, the
 *             day it landed, and where the application has since got to.
 *
 * The server fixes the same two scopes, so the two cannot drift.
 *
 * Same house style as the upgrade and entry exports
 * (upgrade-application/utils/exportUpgrades.ts,
 * application/utils/exportApplications.ts) — one look for every listing the
 * secretariat prints.
 *
 * Dependency-free: the spreadsheet is CSV with a UTF-8 BOM, which Excel opens
 * directly and which renders accented names correctly; the PDF is a styled
 * print window where the browser's own "Save as PDF" does the writing.
 */

/** One row as the export endpoint hands it over. */
export type ExamExportRow = {
  sn: number;
  name?: string | null;
  membership_no?: string | null;
  grade?: string | null;
  email?: string | null;
  phone?: string | null;
  sex?: string | null;
  age?: number | null;
  chapter?: string | null;
  /** Awarded plus claims still pending — the same figure the entry bar reads. */
  cpd_points?: number | null;
  employer?: string | null;
  /** The employer's line of business, not the candidate's post. */
  employer_business?: string | null;
  exam_no?: string | null;
  center?: string | null;
  center_state?: string | null;
  /** As declared on the application — the figure eligibility was read off. */
  graduation_year?: string | number | null;
  /** Years since that graduation, worked out server-side. */
  post_graduate_years?: number | null;
  approved_at?: string | null;
  /* Sent with the paid scope only. */
  status?: string | null;
  reference?: string | null;
  /** Naira — the server converts; the stored figure is in kobo. */
  amount?: number | null;
  paid_at?: string | null;
  qualifications?: {
    qualification?: string | null;
    institution?: string | null;
    country?: string | null;
    completed_on?: string | null;
  }[];
  experience?: {
    position?: string | null;
    employer?: string | null;
    /** The year they joined, for the "year employed" column. */
    year_employed?: string | null;
    from?: string | null;
    to?: string | null;
  }[];
};

export type ExamExportScope = "approved" | "paid";

export type ExamExportMeta = {
  /** e.g. "PPE — Professional Practice Exam". */
  exam: string;
  /** The diet these candidates are sitting. */
  diet: string;
  /** Which listing this is. Defaults to the approved roll. */
  scope?: ExamExportScope;
};

const isPaid = (meta: ExamExportMeta) => meta.scope === "paid";

/** What the document calls itself, on the page and in the file name. */
const noun = (meta: ExamExportMeta) =>
  isPaid(meta)
    ? { title: "Paid Applications", row: "paid application", file: "paid-applications" }
    : { title: "Approved Candidates", row: "approved candidate", file: "approved-candidates" };

const columns = (meta: ExamExportMeta) => [
  "S/N",
  "Membership No",
  "Candidate",
  "Sex",
  "Age",
  "Grade",
  "Chapter",
  "Email",
  "Phone",
  "CPD Points",
  "Exam No",
  "Centre",
  "Year of Graduation",
  "Years of Post-Graduate Experience",
  "Employer",
  "Employer's Business",
  ...(isPaid(meta) ? ["Payment Reference", "Amount (NGN)", "Paid On", "Application Status"] : []),
  "Academic Qualifications",
  "Work Experience",
];

/** "12,500.00". Blank when there is no figure at all, which is not "0.00". */
const money = (amount?: number | null) =>
  amount === null || amount === undefined
    ? ""
    : amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "under_review" is how the column is stored, not how it is read aloud. */
const statusLabel = (status?: string | null) =>
  String(status ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * "BSc Quantity Surveying — Ahmadu Bello University, Zaria (2014)"
 *
 * Each part is dropped rather than stubbed when it is missing: a line reading
 * "— (—)" tells the reader nothing except that the export was written
 * carelessly.
 */
function qualificationLine(q: NonNullable<ExamExportRow["qualifications"]>[number]): string {
  const where = [q.institution, q.country].filter(Boolean).join(", ");
  const head = [q.qualification, where].filter(Boolean).join(" — ");
  return q.completed_on ? `${head} (${q.completed_on})` : head;
}

/** "Senior Quantity Surveyor, Julius Berger (Mar 2018 – Present)" */
function experienceLine(x: NonNullable<ExamExportRow["experience"]>[number]): string {
  const who = [x.position, x.employer].filter(Boolean).join(", ");
  // The span where both ends are known, otherwise the joining year on its own —
  // "employed 2018" says more than a dash with nothing either side of it.
  const when = [x.from, x.to].filter(Boolean).join(" – ") || (x.year_employed ?? "");
  return when ? `${who} (${when})` : who;
}

const qualifications = (r: ExamExportRow) =>
  (r.qualifications ?? []).map(qualificationLine).filter(Boolean);

const experience = (r: ExamExportRow) =>
  (r.experience ?? []).map(experienceLine).filter(Boolean);

/**
 * One row per CANDIDATE, with the two multi-line columns folded into a cell.
 *
 * Not one row per post held: this is a numbered roll of who is sitting, and a
 * candidate with four jobs must be one number on it, not four.
 */
function toRow(r: ExamExportRow, meta: ExamExportMeta): string[] {
  const quals = qualifications(r);
  const exp = experience(r);

  return [
    String(r.sn),
    r.membership_no ?? "",
    r.name ?? "",
    r.sex ?? "",
    // A blank means no date of birth is held. Never 0, which reads as a
    // newborn rather than as "not known".
    r.age == null ? "" : String(r.age),
    r.grade ?? "",
    r.chapter ?? "",
    r.email ?? "",
    r.phone ?? "",
    r.cpd_points == null ? "" : String(r.cpd_points),
    r.exam_no ?? "",
    [r.center, r.center_state].filter(Boolean).join(", "),
    r.graduation_year == null ? "" : String(r.graduation_year),
    r.post_graduate_years == null ? "" : String(r.post_graduate_years),
    r.employer ?? "",
    r.employer_business ?? "",
    ...(isPaid(meta)
      ? [r.reference ?? "", money(r.amount), r.paid_at ?? "", statusLabel(r.status)]
      : []),
    // Newlines inside a quoted CSV field are valid and Excel renders them as
    // separate lines in the one cell, which is what this is.
    quals.join("\n"),
    exp.join("\n"),
  ];
}

function stamp() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    printed: now.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
}

function slug(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
}

function filename(meta: ExamExportMeta, extension: string) {
  return `${noun(meta).file}-${slug(meta.exam)}-${slug(meta.diet)}-${stamp().file}.${extension}`;
}

function heading(meta: ExamExportMeta) {
  return `${noun(meta).title} — ${meta.exam}`;
}

/** "7 paid applications" / "1 approved candidate". */
function tally(rows: unknown[], meta: ExamExportMeta) {
  return `${rows.length} ${noun(meta).row}${rows.length === 1 ? "" : "s"}`;
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const csvLine = (cells: unknown[]) => cells.map(csvCell).join(",");

export function exportExamApplicationsSpreadsheet(
  rows: ExamExportRow[],
  meta: ExamExportMeta,
) {
  const { printed } = stamp();

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    csvLine([`Diet: ${meta.diet}`]),
    csvLine([`${noun(meta).title}: ${rows.length}`]),
    csvLine([`Taken on: ${printed}`]),
    "",
    csvLine(columns(meta)),
    ...rows.map((r) => csvLine(toRow(r, meta))),
  ];

  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A cell's worth of lines as a list, or an em dash when there are none. */
const listCell = (items: string[]) =>
  items.length
    ? `<ul>${items.map((t) => `<li>${esc(t)}</li>`).join("")}</ul>`
    : `<span class="none">—</span>`;

export function exportExamApplicationsPdf(rows: ExamExportRow[], meta: ExamExportMeta) {
  const { printed } = stamp();
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to export a PDF.");
    return;
  }

  const paid = isPaid(meta);
  const columnCount = paid ? 10 : 8;

  const body = rows.length
    ? rows
        .map(
          (r) => `<tr>
            <td class="sn">${r.sn}</td>
            <td>
              <b>${esc(r.name ?? "—")}</b>
              ${r.membership_no ? `<div class="sub">${esc(r.membership_no)}</div>` : ""}
              ${[r.grade, r.sex, r.age == null ? "" : `${r.age} yrs`]
                .filter(Boolean)
                .map((v) => esc(String(v)))
                .join(" &middot; ")
                .replace(/^(.+)$/, '<div class="sub">$1</div>')}
              ${r.chapter ? `<div class="sub">${esc(r.chapter)} Chapter</div>` : ""}
              ${[r.email, r.phone]
                .filter(Boolean)
                .map((v) => esc(String(v)))
                .join(" &middot; ")
                .replace(/^(.+)$/, '<div class="sub">$1</div>')}
            </td>
            <td class="mono">${esc(r.exam_no ?? "—")}</td>
            <td>${esc([r.center, r.center_state].filter(Boolean).join(", ")) || "—"}</td>
            <td class="year">${esc(r.graduation_year ?? "") || "—"}
              ${r.post_graduate_years == null ? "" : `<div class="sub">${r.post_graduate_years} yrs post-grad</div>`}</td>
            <td class="year">${r.cpd_points == null ? "—" : esc(String(r.cpd_points))}</td>
            ${
              paid
                ? `<td class="mono">${esc(r.reference ?? "—")}
                     ${r.paid_at ? `<div class="sub">${esc(r.paid_at)}</div>` : ""}</td>
                   <td class="amount">${esc(money(r.amount)) || "—"}
                     <div class="sub">${esc(statusLabel(r.status)) || "—"}</div></td>`
                : ""
            }
            <td class="wide">${listCell(qualifications(r))}</td>
            <td class="wide">${listCell(experience(r))}
              ${r.employer_business ? `<div class="sub">Business: ${esc(r.employer_business)}</div>` : ""}</td>
          </tr>`,
        )
        .join("")
    : `<tr><td class="empty" colspan="${columnCount}">No ${noun(meta).row}s in this diet.</td></tr>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(meta, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 landscape;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    .sub-head{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub-head b{color:#111}
    table{width:100%;border-collapse:collapse}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    td.sn{width:28px;text-align:right;font-weight:900;color:#666}
    td.mono{font-family:'Courier New',monospace;white-space:nowrap}
    td.amount{text-align:right;white-space:nowrap;font-weight:700}
    td.year{width:42px;text-align:center;white-space:nowrap}
    td.wide{width:27%}
    td.empty{text-align:center;color:#999;padding:24px}
    .sub{font-size:8px;color:#888;margin-top:1px}
    .none{color:#ccc}
    ul{margin:0;padding-left:12px}
    li{margin-bottom:2px;line-height:1.35}
    li:last-child{margin-bottom:0}
    /* A candidate's qualifications and posts belong together on one page —
       split across a break, the second half reads as somebody else's. */
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>${esc(heading(meta))}</h1>
    <div class="sub-head">
      Diet: <b>${esc(meta.diet)}</b> &middot; ${esc(tally(rows, meta))}<br>
      Taken on ${esc(printed)}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>S/N</th>
        <th>Candidate</th>
        <th>Exam No</th>
        <th>Centre</th>
        <th>Grad. Year</th>
        <th>CPD</th>
        ${paid ? `<th>Reference &middot; Paid On</th><th>Amount &middot; Status</th>` : ""}
        <th>Academic Qualifications</th>
        <th>Work Experience</th>
      </tr>
    </thead>
    <tbody>${body}</tbody>
  </table>

  <div class="foot">NIQS Portal &middot; ${esc(noun(meta).title)}</div>
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 6000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
